import React, { useState, useEffect, useCallback } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";
import { useBranch } from "../../../contexts/BranchContext";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => `$${(parseFloat(n) || 0)?.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtNum = (n) => (parseFloat(n) || 0)?.toFixed(2);

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const SALARY_TYPES = [{ value: "fixed", label: "Fixed" }, { value: "hourly", label: "Hourly" }];
const PAYMENT_CYCLES = [{ value: "monthly", label: "Monthly" }, { value: "weekly", label: "Weekly" }, { value: "bi_weekly", label: "Bi-weekly" }];
const PAYMENT_METHODS = [{ value: "cash", label: "Cash" }, { value: "bank_transfer", label: "Bank Transfer" }, { value: "upi", label: "UPI" }];
const COMMISSION_TYPES = [{ value: "flat_per_class", label: "Flat per Class" }, { value: "percent_member_fees", label: "% of Member Fees" }, { value: "hybrid", label: "Hybrid" }];
const DONUT_COLORS = ["var(--color-primary)", "var(--color-accent)", "var(--color-secondary)", "var(--color-warning)"];

function SkeletonRow({ cols = 7 }) {
  return (
    <tr className="border-b border-[var(--color-border)]">
      {[...Array(cols)]?.map((_, i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 bg-[var(--color-muted)] rounded animate-pulse" style={{ width: i === 0 ? "120px" : "80px" }} />
        </td>
      ))}
    </tr>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid: "bg-green-100 text-green-700",
    pending: "bg-yellow-100 text-yellow-700",
    overdue: "bg-red-100 text-red-700",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${map?.[status] || "bg-gray-100 text-gray-600"}`}>
      {status}
    </span>
  );
}

// ─── Salary Modal ──────────────────────────────────────────────────────────────
function SalaryModal({ open, onClose, onSaved, editData, staffList }) {
  const today = new Date()?.toISOString()?.split("T")?.[0];
  const empty = { staff_id: "", base_salary: "", salary_type: "fixed", payment_cycle: "monthly", deductions: "", deduction_notes: "", effective_date: today };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (editData) {
      setForm({
        staff_id: editData?.staff_id || "",
        base_salary: editData?.base_salary ? String(editData?.base_salary) : "",
        salary_type: editData?.salary_type || "fixed",
        payment_cycle: editData?.payment_cycle || "monthly",
        deductions: editData?.deductions ? String(editData?.deductions) : "",
        deduction_notes: editData?.deduction_notes || "",
        effective_date: editData?.effective_date || today,
      });
    } else {
      setForm(empty);
    }
    setError(null);
  }, [editData, open]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form?.staff_id) { setError("Please select a staff member."); return; }
    if (!form?.base_salary || isNaN(parseFloat(form?.base_salary))) { setError("Enter a valid base salary."); return; }
    setSaving(true);
    try {
      const payload = {
        staff_id: form?.staff_id,
        base_salary: parseFloat(form?.base_salary),
        salary_type: form?.salary_type,
        payment_cycle: form?.payment_cycle,
        deductions: parseFloat(form?.deductions) || 0,
        deduction_notes: form?.deduction_notes || null,
        effective_date: form?.effective_date,
      };
      await api.post('/payroll/salaries', payload);
      onSaved();
      onClose();
    } catch (e) {
      setError(e?.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-[var(--color-card)] rounded-[var(--radius)] shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-[var(--color-border)]">
          <h3 className="text-base font-bold text-[var(--color-foreground)]">{editData ? "Edit Salary Record" : "Add Salary Record"}</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[var(--color-muted)] transition-colors"><Icon name="X" size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}
          <div>
            <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1">Staff Member *</label>
            <select value={form?.staff_id} onChange={e => set("staff_id", e?.target?.value)} disabled={!!editData}
              className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]">
              <option value="">Select staff...</option>
              {staffList?.map(s => <option key={s?.id} value={s?.id}>{s?.full_name} ({s?.role})</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1">Base Salary *</label>
              <input type="number" value={form?.base_salary} onChange={e => set("base_salary", e?.target?.value)} placeholder="0.00"
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1">Deductions</label>
              <input type="number" value={form?.deductions} onChange={e => set("deductions", e?.target?.value)} placeholder="0.00"
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1">Salary Type</label>
              <select value={form?.salary_type} onChange={e => set("salary_type", e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]">
                {SALARY_TYPES?.map(t => <option key={t?.value} value={t?.value}>{t?.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1">Payment Cycle</label>
              <select value={form?.payment_cycle} onChange={e => set("payment_cycle", e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]">
                {PAYMENT_CYCLES?.map(c => <option key={c?.value} value={c?.value}>{c?.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1">Deduction Notes (Tax, Advance, etc.)</label>
            <input type="text" value={form?.deduction_notes} onChange={e => set("deduction_notes", e?.target?.value)} placeholder="e.g. Tax 10%, Advance $200"
              className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1">Effective Date</label>
            <input type="date" value={form?.effective_date} onChange={e => set("effective_date", e?.target?.value)}
              className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-[var(--color-border)]">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-[var(--color-border)] rounded-md hover:bg-[var(--color-muted)] transition-colors">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 text-sm bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-50">
            {saving ? "Saving..." : "Save Record"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Mark Paid Modal ───────────────────────────────────────────────────────────
function MarkPaidModal({ open, onClose, onSaved, staffRow }) {
  const today = new Date()?.toISOString()?.split("T")?.[0];
  const now = new Date();
  const [form, setForm] = useState({ payment_method: "cash", payment_date: today, notes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setForm({ payment_method: "cash", payment_date: today, notes: "" }); setError(null); }, [open]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const netAmount = (parseFloat(staffRow?.base_salary) || 0) + (parseFloat(staffRow?.commission) || 0) - (parseFloat(staffRow?.deductions) || 0);
      const payload = {
        staff_id: staffRow?.id,
        month: now?.getMonth() + 1,
        year: now?.getFullYear(),
        base_salary: parseFloat(staffRow?.base_salary) || 0,
        commission_amount: parseFloat(staffRow?.commission) || 0,
        deductions: parseFloat(staffRow?.deductions) || 0,
        net_amount: netAmount,
        payment_method: form?.payment_method,
        payment_date: form?.payment_date,
        status: "paid",
        notes: form?.notes || null,
      };
      await api.post('/payroll', payload);
      onSaved();
      onClose();
    } catch (e) {
      setError(e?.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  const net = (parseFloat(staffRow?.base_salary) || 0) + (parseFloat(staffRow?.commission) || 0) - (parseFloat(staffRow?.deductions) || 0);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-[var(--color-card)] rounded-[var(--radius)] shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-[var(--color-border)]">
          <h3 className="text-base font-bold text-[var(--color-foreground)]">Mark as Paid</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[var(--color-muted)] transition-colors"><Icon name="X" size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}
          <div className="p-3 bg-[var(--color-muted)]/40 rounded-lg space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-[var(--color-muted-foreground)]">Staff:</span><span className="font-medium">{staffRow?.full_name}</span></div>
            <div className="flex justify-between"><span className="text-[var(--color-muted-foreground)]">Base Salary:</span><span>{fmt(staffRow?.base_salary)}</span></div>
            <div className="flex justify-between"><span className="text-[var(--color-muted-foreground)]">Commission:</span><span>{fmt(staffRow?.commission)}</span></div>
            <div className="flex justify-between"><span className="text-[var(--color-muted-foreground)]">Deductions:</span><span className="text-red-600">-{fmt(staffRow?.deductions)}</span></div>
            <div className="flex justify-between border-t border-[var(--color-border)] pt-1 mt-1"><span className="font-semibold">Net Payable:</span><span className="font-bold text-[var(--color-primary)]">{fmt(net)}</span></div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1">Payment Method</label>
            <select value={form?.payment_method} onChange={e => set("payment_method", e?.target?.value)}
              className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]">
              {PAYMENT_METHODS?.map(m => <option key={m?.value} value={m?.value}>{m?.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1">Payment Date</label>
            <input type="date" value={form?.payment_date} onChange={e => set("payment_date", e?.target?.value)}
              className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--color-muted-foreground)] mb-1">Notes</label>
            <input type="text" value={form?.notes} onChange={e => set("notes", e?.target?.value)} placeholder="Optional notes"
              className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-[var(--color-border)]">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-[var(--color-border)] rounded-md hover:bg-[var(--color-muted)] transition-colors">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50">
            {saving ? "Processing..." : "Confirm Payment"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function PayrollManagement() {
  useAuth();
  const { selectedBranchId } = useBranch();
  const now = new Date();
  const currentMonth = now?.getMonth() + 1;
  const currentYear = now?.getFullYear();

  const [activeTab, setActiveTab] = useState("salaries");
  const [staff, setStaff] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState({ salaries: true, commissions: true, payments: true });
  const [error, setError] = useState(null);

  // Modals
  const [salaryModal, setSalaryModal] = useState({ open: false, editData: null });
  const [markPaidModal, setMarkPaidModal] = useState({ open: false, staffRow: null });
  const [payslipStaff, setPayslipStaff] = useState(null);

  // Filters
  const [filterMonth, setFilterMonth] = useState(currentMonth);
  const [filterYear, setFilterYear] = useState(currentYear);
  const [filterStaff, setFilterStaff] = useState("all");

  // ── Fetch staff ──────────────────────────────────────────────────────────────
  const fetchStaff = useCallback(async () => {
    try {
      const params = {};
      if (selectedBranchId) params.branch_id = selectedBranchId;
      const { data } = await api.get('/staff', { params });
      setStaff(data || []);
    } catch (err) {
      console.error("Failed to load staff:", err);
    }
  }, [selectedBranchId]);

  // ── Fetch salaries ───────────────────────────────────────────────────────────
  const fetchSalaries = useCallback(async () => {
    setLoading(p => ({ ...p, salaries: true }));
    try {
      const salaryParams = {};
      if (selectedBranchId) salaryParams.branch_id = selectedBranchId;
      const payParams = { month: currentMonth, year: currentYear };
      if (selectedBranchId) payParams.branch_id = selectedBranchId;
      const { data: salaryData } = await api.get('/payroll/salaries', { params: salaryParams });
      const { data: payData } = await api.get('/payroll', { params: payParams });

      const paidSet = new Set((payData || []).filter(p => p.status === "paid").map(p => p.staff_id));

      const enriched = (salaryData || [])?.map(s => {
        const commission = 0;
        const deductions = parseFloat(s?.deductions) || 0;
        const base = parseFloat(s?.base_salary) || 0;
        const net = base + commission - deductions;
        const payStatus = paidSet?.has(s?.staff_id) ? "paid" : "pending";
        return {
          ...s,
          full_name: s?.staff_name || "Unknown",
          role: s?.staff_role || "staff",
          commission,
          net_payable: net,
          pay_status: payStatus,
        };
      });
      setSalaries(enriched);
    } catch (e) {
      setError(e?.message);
    } finally {
      setLoading(p => ({ ...p, salaries: false }));
    }
  }, [currentMonth, currentYear, selectedBranchId]);

  // ── Fetch commissions ────────────────────────────────────────────────────────
  const fetchCommissions = useCallback(async () => {
    setLoading(p => ({ ...p, commissions: true }));
    setCommissions([]);
    setLoading(p => ({ ...p, commissions: false }));
  }, []);

  // ── Recalculate commissions ──────────────────────────────────────────────────
  const recalculateCommissions = async () => {
    // No backend endpoint for commission recalculation
  };

  // ── Fetch payments ───────────────────────────────────────────────────────────
  const fetchPayments = useCallback(async () => {
    setLoading(p => ({ ...p, payments: true }));
    try {
      const params = {};
      if (filterMonth !== "all") params.month = filterMonth;
      if (filterYear !== "all") params.year = filterYear;
      if (filterStaff !== "all") params.staff_id = filterStaff;
      if (selectedBranchId) params.branch_id = selectedBranchId;
      const { data } = await api.get('/payroll', { params });
      setPayments(data || []);
    } catch (e) {
      setError(e?.message);
    } finally {
      setLoading(p => ({ ...p, payments: false }));
    }
  }, [filterMonth, filterYear, filterStaff, selectedBranchId]);

  useEffect(() => { fetchStaff(); fetchSalaries(); fetchCommissions(); fetchPayments(); }, [fetchStaff, fetchSalaries, fetchCommissions, fetchPayments]);
  useEffect(() => { fetchPayments(); }, [filterMonth, filterYear, filterStaff]);

  // ── KPI calculations ─────────────────────────────────────────────────────────
  const thisMonthPayments = payments?.filter(p => p?.month === currentMonth && p?.year === currentYear);
  const totalPayrollThisMonth = thisMonthPayments?.reduce((s, p) => s + (parseFloat(p?.net_amount) || 0), 0);
  const totalCommissions = thisMonthPayments?.reduce((s, p) => s + (parseFloat(p?.commission_amount) || 0), 0);
  const pendingCount = salaries?.filter(s => s?.pay_status === "pending")?.length;
  const avgSalary = salaries?.length ? salaries?.reduce((s, r) => s + (parseFloat(r?.base_salary) || 0), 0) / salaries?.length : 0;

  // ── Trend data (last 6 months) ───────────────────────────────────────────────
  const trendData = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(currentYear, currentMonth - 1 - (5 - i), 1);
    const m = d?.getMonth() + 1;
    const y = d?.getFullYear();
    const total = payments?.filter(p => p?.month === m && p?.year === y)?.reduce((s, p) => s + (parseFloat(p?.net_amount) || 0), 0);
    return { name: MONTHS?.[m - 1], total };
  });

  // ── Donut data ───────────────────────────────────────────────────────────────
  const staffCost = salaries?.filter(s => s?.role === "staff")?.reduce((s, r) => s + (parseFloat(r?.base_salary) || 0), 0);
  const trainerCost = salaries?.filter(s => s?.role === "trainer")?.reduce((s, r) => s + (parseFloat(r?.base_salary) || 0), 0);
  const donutData = [{ name: "Staff", value: staffCost }, { name: "Trainers", value: trainerCost }]?.filter(d => d?.value > 0);

  // ── Generate payslip (jsPDF) ─────────────────────────────────────────────────
  const generatePayslip = async (row) => {
    try {
      const { default: jsPDF } = await import("jspdf");
      const doc = new jsPDF();
      const pageW = doc?.internal?.pageSize?.getWidth();
      const net = (parseFloat(row?.base_salary) || 0) + (parseFloat(row?.commission) || 0) - (parseFloat(row?.deductions) || 0);

      // Header
      doc?.setFillColor(79, 70, 229);
      doc?.rect(0, 0, pageW, 40, "F");
      doc?.setTextColor(255, 255, 255);
      doc?.setFontSize(20);
      doc?.setFont("helvetica", "bold");
      doc?.text("GYM PAYSLIP", pageW / 2, 18, { align: "center" });
      doc?.setFontSize(10);
      doc?.setFont("helvetica", "normal");
      doc?.text(`${MONTHS?.[currentMonth - 1]} ${currentYear}`, pageW / 2, 30, { align: "center" });

      // Employee info
      doc?.setTextColor(30, 30, 30);
      doc?.setFontSize(12);
      doc?.setFont("helvetica", "bold");
      doc?.text("Employee Details", 14, 55);
      doc?.setDrawColor(200, 200, 200);
      doc?.line(14, 57, pageW - 14, 57);
      doc?.setFont("helvetica", "normal");
      doc?.setFontSize(10);
      const infoRows = [
        ["Name:", row?.full_name || row?.staff?.full_name || "N/A"],
        ["Role:", (row?.role || "staff")?.toUpperCase()],
        ["Employee ID:", (row?.staff_id || row?.id || "")?.substring(0, 8)?.toUpperCase()],
        ["Pay Period:", `${MONTHS?.[currentMonth - 1]} ${currentYear}`],
        ["Payment Cycle:", (row?.payment_cycle || "monthly")?.toUpperCase()],
      ];
      infoRows?.forEach(([label, val], i) => {
        doc?.setFont("helvetica", "bold");
        doc?.text(label, 14, 65 + i * 8);
        doc?.setFont("helvetica", "normal");
        doc?.text(val, 60, 65 + i * 8);
      });

      // Earnings
      doc?.setFont("helvetica", "bold");
      doc?.setFontSize(12);
      doc?.text("Earnings", 14, 115);
      doc?.line(14, 117, pageW - 14, 117);
      doc?.setFontSize(10);
      const earningsRows = [
        ["Base Salary", fmt(row?.base_salary)],
        ["Commission", fmt(row?.commission)],
        ["Bonuses", "$0.00"],
      ];
      earningsRows?.forEach(([label, val], i) => {
        doc?.setFont("helvetica", "normal");
        doc?.text(label, 14, 125 + i * 8);
        doc?.text(val, pageW - 14, 125 + i * 8, { align: "right" });
      });

      // Deductions
      doc?.setFont("helvetica", "bold");
      doc?.setFontSize(12);
      doc?.text("Deductions", 14, 160);
      doc?.line(14, 162, pageW - 14, 162);
      doc?.setFontSize(10);
      const dedRows = [
        ["Tax / Advance", fmt(row?.deductions)],
        ["Other", "$0.00"],
      ];
      dedRows?.forEach(([label, val], i) => {
        doc?.setFont("helvetica", "normal");
        doc?.text(label, 14, 170 + i * 8);
        doc?.text(val, pageW - 14, 170 + i * 8, { align: "right" });
      });

      // Net Pay
      doc?.setFillColor(79, 70, 229);
      doc?.rect(14, 192, pageW - 28, 16, "F");
      doc?.setTextColor(255, 255, 255);
      doc?.setFont("helvetica", "bold");
      doc?.setFontSize(13);
      doc?.text("NET PAY", 20, 203);
      doc?.text(fmt(net), pageW - 20, 203, { align: "right" });

      // Footer
      doc?.setTextColor(120, 120, 120);
      doc?.setFont("helvetica", "normal");
      doc?.setFontSize(9);
      doc?.text("This is a computer-generated payslip. No signature required.", pageW / 2, 225, { align: "center" });
      doc?.line(14, 235, pageW - 14, 235);
      doc?.text("Authorized Signature: ____________________________", 14, 245);

      doc?.save(`payslip_${(row?.full_name || "staff")?.replace(/\s+/g, "_")}_${MONTHS?.[currentMonth - 1]}${currentYear}.pdf`);
    } catch (e) {
      console.error("PDF generation error:", e);
      alert("Could not generate payslip. Please try again.");
    }
  };

  const tabs = [
    { key: "salaries", label: "Salary Tracking", icon: "Users" },
    { key: "commissions", label: "Commissions", icon: "TrendingUp" },
    { key: "history", label: "Payment History", icon: "History" },
    { key: "dashboard", label: "Summary", icon: "BarChart2" },
  ];

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex items-center gap-1 border-b border-[var(--color-border)] overflow-x-auto scrollbar-hide">
        {tabs?.map(t => (
          <button key={t?.key} onClick={() => setActiveTab(t?.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === t?.key ? "border-[var(--color-primary)] text-[var(--color-primary)]" : "border-transparent text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"}`}>
            <Icon name={t?.icon} size={14} color={activeTab === t?.key ? "var(--color-primary)" : "currentColor"} />
            {t?.label}
          </button>
        ))}
      </div>
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <Icon name="AlertCircle" size={14} color="#dc2626" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto"><Icon name="X" size={14} /></button>
        </div>
      )}
      {/* ── SALARY TRACKING ─────────────────────────────────────────────────── */}
      {activeTab === "salaries" && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-bold text-[var(--color-foreground)]">Staff Salary Tracking</h2>
              <p className="text-xs text-[var(--color-muted-foreground)]">{MONTHS?.[currentMonth - 1]} {currentYear} payroll</p>
            </div>
            <button onClick={() => setSalaryModal({ open: true, editData: null })}
              className="flex items-center gap-1.5 px-3 py-2 text-sm bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors">
              <Icon name="Plus" size={14} color="white" />
              Add Salary Record
            </button>
          </div>

          <div className="bg-[var(--color-card)] rounded-[var(--radius)] border border-[var(--color-border)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--color-border)] bg-[var(--color-muted)]/30">
                    {["Staff Name", "Role", "Base Salary", "Commission", "Deductions", "Net Payable", "Status", "Actions"]?.map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[var(--color-muted-foreground)] whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading?.salaries ? (
                    [...Array(4)]?.map((_, i) => <SkeletonRow key={i} cols={8} />)
                  ) : salaries?.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <Icon name="Users" size={32} color="var(--color-muted-foreground)" />
                        <p className="text-sm text-[var(--color-muted-foreground)]">No salary records yet.</p>
                        <button onClick={() => setSalaryModal({ open: true, editData: null })} className="text-xs text-[var(--color-primary)] underline">Add first record</button>
                      </div>
                    </td></tr>
                  ) : salaries?.map(row => (
                    <tr key={row?.id} className="border-b border-[var(--color-border)] hover:bg-[var(--color-muted)]/20 transition-colors">
                      <td className="px-4 py-3 font-medium text-[var(--color-foreground)]">{row?.full_name}</td>
                      <td className="px-4 py-3"><span className="capitalize text-xs px-2 py-0.5 rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)]">{row?.role}</span></td>
                      <td className="px-4 py-3">{fmt(row?.base_salary)}</td>
                      <td className="px-4 py-3 text-green-600">{fmt(row?.commission)}</td>
                      <td className="px-4 py-3 text-red-600">-{fmt(row?.deductions)}</td>
                      <td className="px-4 py-3 font-semibold text-[var(--color-primary)]">{fmt(row?.net_payable)}</td>
                      <td className="px-4 py-3"><StatusBadge status={row?.pay_status} /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => generatePayslip(row)} title="Generate Payslip"
                            className="p-1.5 rounded hover:bg-[var(--color-muted)] transition-colors text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
                            <Icon name="FileText" size={14} />
                          </button>
                          {row?.pay_status !== "paid" && (
                            <button onClick={() => setMarkPaidModal({ open: true, staffRow: row })} title="Mark as Paid"
                              className="p-1.5 rounded hover:bg-green-50 transition-colors text-green-600">
                              <Icon name="CheckCircle" size={14} />
                            </button>
                          )}
                          <button onClick={() => setSalaryModal({ open: true, editData: row })} title="Edit"
                            className="p-1.5 rounded hover:bg-[var(--color-muted)] transition-colors text-[var(--color-muted-foreground)]">
                            <Icon name="Pencil" size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {/* ── COMMISSIONS ─────────────────────────────────────────────────────── */}
      {activeTab === "commissions" && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-bold text-[var(--color-foreground)]">Commission Calculations</h2>
              <p className="text-xs text-[var(--color-muted-foreground)]">Auto-calculated for {MONTHS?.[currentMonth - 1]} {currentYear}</p>
            </div>
            <button onClick={recalculateCommissions}
              className="flex items-center gap-1.5 px-3 py-2 text-sm bg-[var(--color-accent)] text-white rounded-md hover:opacity-90 transition-opacity">
              <Icon name="RefreshCw" size={14} color="white" />
              Recalculate
            </button>
          </div>

          <div className="bg-[var(--color-card)] rounded-[var(--radius)] border border-[var(--color-border)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--color-border)] bg-[var(--color-muted)]/30">
                    {["Trainer", "Classes Conducted", "Members Assigned", "Avg Rating", "Commission Type", "Rate", "Total Commission", "Status"]?.map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[var(--color-muted-foreground)] whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading?.commissions ? (
                    [...Array(3)]?.map((_, i) => <SkeletonRow key={i} cols={8} />)
                  ) : commissions?.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <Icon name="TrendingUp" size={32} color="var(--color-muted-foreground)" />
                        <p className="text-sm text-[var(--color-muted-foreground)]">No trainers found for commission calculation.</p>
                      </div>
                    </td></tr>
                  ) : commissions?.map(row => (
                    <tr key={row?.id} className="border-b border-[var(--color-border)] hover:bg-[var(--color-muted)]/20 transition-colors">
                      <td className="px-4 py-3 font-medium text-[var(--color-foreground)]">{row?.full_name}</td>
                      <td className="px-4 py-3">{row?.classes_count}</td>
                      <td className="px-4 py-3">{row?.members_count}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <Icon name="Star" size={12} color="#f59e0b" />
                          <span>{fmtNum(row?.avg_rating)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs capitalize">{(row?.commission_type || "flat_per_class")?.replace(/_/g, " ")}</td>
                      <td className="px-4 py-3">{fmt(row?.commission_rate)}</td>
                      <td className="px-4 py-3 font-semibold text-green-600">{fmt(row?.total_commission)}</td>
                      <td className="px-4 py-3"><StatusBadge status={row?.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {/* ── PAYMENT HISTORY ─────────────────────────────────────────────────── */}
      {activeTab === "history" && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-bold text-[var(--color-foreground)]">Payment History</h2>
              <p className="text-xs text-[var(--color-muted-foreground)]">All payroll payment records</p>
            </div>
            {/* KPI */}
            <div className="flex items-center gap-2 px-3 py-2 bg-[var(--color-primary)]/10 rounded-lg">
              <Icon name="DollarSign" size={14} color="var(--color-primary)" />
              <span className="text-xs text-[var(--color-muted-foreground)]">This Month:</span>
              <span className="text-sm font-bold text-[var(--color-primary)]">{fmt(totalPayrollThisMonth)}</span>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <select value={filterMonth} onChange={e => setFilterMonth(e?.target?.value === "all" ? "all" : parseInt(e?.target?.value))}
              className="px-3 py-1.5 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]">
              <option value="all">All Months</option>
              {MONTHS?.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            <select value={filterYear} onChange={e => setFilterYear(e?.target?.value === "all" ? "all" : parseInt(e?.target?.value))}
              className="px-3 py-1.5 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]">
              <option value="all">All Years</option>
              {[currentYear, currentYear - 1]?.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select value={filterStaff} onChange={e => setFilterStaff(e?.target?.value)}
              className="px-3 py-1.5 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]">
              <option value="all">All Staff</option>
              {staff?.map(s => <option key={s?.id} value={s?.id}>{s?.full_name}</option>)}
            </select>
          </div>

          <div className="bg-[var(--color-card)] rounded-[var(--radius)] border border-[var(--color-border)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--color-border)] bg-[var(--color-muted)]/30">
                    {["Staff Name", "Month/Year", "Base Salary", "Commission", "Deductions", "Net Amount", "Method", "Payment Date", "Status"]?.map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[var(--color-muted-foreground)] whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading?.payments ? (
                    [...Array(4)]?.map((_, i) => <SkeletonRow key={i} cols={9} />)
                  ) : payments?.length === 0 ? (
                    <tr><td colSpan={9} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <Icon name="History" size={32} color="var(--color-muted-foreground)" />
                        <p className="text-sm text-[var(--color-muted-foreground)]">No payment records found.</p>
                      </div>
                    </td></tr>
                  ) : payments?.map(p => (
                    <tr key={p?.id} className="border-b border-[var(--color-border)] hover:bg-[var(--color-muted)]/20 transition-colors">
                      <td className="px-4 py-3 font-medium text-[var(--color-foreground)]">{p?.staff_name}</td>
                      <td className="px-4 py-3 text-[var(--color-muted-foreground)]">{MONTHS?.[(p?.month || 1) - 1]} {p?.year}</td>
                      <td className="px-4 py-3">{fmt(p?.base_salary)}</td>
                      <td className="px-4 py-3 text-green-600">{fmt(p?.commission_amount)}</td>
                      <td className="px-4 py-3 text-red-600">-{fmt(p?.deductions)}</td>
                      <td className="px-4 py-3 font-semibold">{fmt(p?.net_amount)}</td>
                      <td className="px-4 py-3 capitalize text-xs">{(p?.payment_method || "cash")?.replace(/_/g, " ")}</td>
                      <td className="px-4 py-3 text-[var(--color-muted-foreground)]">{p?.payment_date || "—"}</td>
                      <td className="px-4 py-3"><StatusBadge status={p?.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {/* ── SUMMARY DASHBOARD ───────────────────────────────────────────────── */}
      {activeTab === "dashboard" && (
        <div className="space-y-6">
          <h2 className="text-base font-bold text-[var(--color-foreground)]">Payroll Summary Dashboard</h2>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Total Payroll This Month", value: fmt(totalPayrollThisMonth), icon: "DollarSign", color: "var(--color-primary)" },
              { label: "Total Commissions", value: fmt(totalCommissions), icon: "TrendingUp", color: "var(--color-accent)" },
              { label: "Pending Payments", value: String(pendingCount), icon: "Clock", color: "var(--color-warning)" },
              { label: "Avg Base Salary", value: fmt(avgSalary), icon: "Users", color: "var(--color-secondary)" },
            ]?.map((kpi, i) => (
              <div key={i} className="bg-[var(--color-card)] rounded-[var(--radius)] border border-[var(--color-border)] p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${kpi?.color}18` }}>
                  <Icon name={kpi?.icon} size={18} color={kpi?.color} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-[var(--color-muted-foreground)] truncate">{kpi?.label}</p>
                  <p className="text-lg font-bold text-[var(--color-foreground)]">{kpi?.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Trend Bar Chart */}
            <div className="bg-[var(--color-card)] rounded-[var(--radius)] border border-[var(--color-border)] p-5">
              <h3 className="text-sm font-semibold text-[var(--color-foreground)] mb-4">Payroll Cost Trend (Last 6 Months)</h3>
              {trendData?.every(d => d?.total === 0) ? (
                <div className="flex flex-col items-center justify-center h-48 gap-2">
                  <Icon name="BarChart2" size={32} color="var(--color-muted-foreground)" />
                  <p className="text-sm text-[var(--color-muted-foreground)]">No payment data yet</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={trendData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--color-muted-foreground)" }} />
                    <YAxis tick={{ fontSize: 11, fill: "var(--color-muted-foreground)" }} tickFormatter={v => `$${(v / 1000)?.toFixed(0)}k`} />
                    <Tooltip formatter={v => [fmt(v), "Payroll"]} contentStyle={{ background: "var(--color-card)", border: "1px solid var(--color-border)", borderRadius: "6px", fontSize: "12px" }} />
                    <Bar dataKey="total" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Donut Chart */}
            <div className="bg-[var(--color-card)] rounded-[var(--radius)] border border-[var(--color-border)] p-5">
              <h3 className="text-sm font-semibold text-[var(--color-foreground)] mb-4">Staff Cost Breakdown by Role</h3>
              {donutData?.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 gap-2">
                  <Icon name="PieChart" size={32} color="var(--color-muted-foreground)" />
                  <p className="text-sm text-[var(--color-muted-foreground)]">No salary data yet</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={donutData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={4} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`} labelLine={false}>
                      {donutData?.map((_, i) => <Cell key={i} fill={DONUT_COLORS?.[i % DONUT_COLORS?.length]} />)}
                    </Pie>
                    <Tooltip formatter={v => [fmt(v), "Base Salary"]} contentStyle={{ background: "var(--color-card)", border: "1px solid var(--color-border)", borderRadius: "6px", fontSize: "12px" }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Modals */}
      <SalaryModal
        open={salaryModal?.open}
        onClose={() => setSalaryModal({ open: false, editData: null })}
        onSaved={fetchSalaries}
        editData={salaryModal?.editData}
        staffList={staff}
      />
      <MarkPaidModal
        open={markPaidModal?.open}
        onClose={() => setMarkPaidModal({ open: false, staffRow: null })}
        onSaved={() => { fetchSalaries(); fetchPayments(); }}
        staffRow={markPaidModal?.staffRow}
      />
    </div>
  );
}
